import { useQueryClient } from "@tanstack/react-query";
import { useMemo } from "react";
import { SmsMessage } from "../chat/types/SmsMessage";
import { getSmsContactsQueryKey } from "../contacts/hooks/useSmsContactsQuery";
import { CHAT_SMS_QUERY_KEY, getChatSmsQueryKey } from "../chat/hooks/useChatSmsQuery";
import { CompanyNumbersByCompany } from "@components/virtual-receptionist/porting-request/hooks/useCompanyNumbersMultiQuery";
import { useWatchSmsVrNotification } from "src/notifications/hooks/useWatchSmsVrNotification";
import { useAppSelector } from "src/redux/hooks/redux";

export type SmsMessageWithCompany = SmsMessage & {
  companyName?: string;
};

type ResolvedOwnership = {
  ownedNumber: string;
  contactPhone: string;
  isIncoming: boolean;
};

const normalizePhone = (phone?: string | null): string => {
  return String(phone ?? "").replace(/\D/g, "");
};

const areSamePhone = (phoneA?: string | null, phoneB?: string | null): boolean => {
  const normalizedA = normalizePhone(phoneA);
  const normalizedB = normalizePhone(phoneB);

  if (!normalizedA || !normalizedB) return false;

  if (normalizedA.length >= 10 && normalizedB.length >= 10) {
    return normalizedA.slice(-10) === normalizedB.slice(-10);
  }

  return normalizedA === normalizedB;
};

const resolveOwnership = (
  sms: SmsMessage,
  ownedNumbers: string[],
  fallbackNumber?: string,
): ResolvedOwnership | null => {
  if (!sms?.from || !sms?.to) return null;

  const candidates = fallbackNumber ? [...ownedNumbers, fallbackNumber] : ownedNumbers;

  const inboundMatch = candidates.find((candidate) => areSamePhone(sms.to, candidate));
  if (inboundMatch) {
    return { ownedNumber: inboundMatch, contactPhone: sms.from, isIncoming: true };
  }

  const outboundMatch = candidates.find((candidate) => areSamePhone(sms.from, candidate));
  if (outboundMatch) {
    return { ownedNumber: outboundMatch, contactPhone: sms.to, isIncoming: false };
  }

  return null;
};

export const useSmsSocket = (
  sessionNumber?: string,
  companieNumbers: CompanyNumbersByCompany[] = [],
  onIncomingSms?: (sms: SmsMessageWithCompany) => void,
) => {
  const queryClient = useQueryClient();

  const userCompanies = useAppSelector((state) => state.userState.companies);

  const ownedNumbers = useMemo<string[]>(
    () => companieNumbers.flatMap((company) => (company.phoneNumbers ?? []).map((number) => number.phoneNumber)),
    [companieNumbers],
  );

  const patchMessageInChatCache = (sms: SmsMessage) => {
    const ownership = resolveOwnership(sms, ownedNumbers, sessionNumber);
    if (!ownership) return;

    const queryKeyPrefix = getChatSmsQueryKey(ownership.contactPhone, ownership.ownedNumber, sms.companyId, "");

    queryClient.setQueriesData(
      {
        predicate: (query) => {
          const key = query.queryKey;
          return (
            Array.isArray(key) &&
            key[0] === CHAT_SMS_QUERY_KEY[0] &&
            key[1] === CHAT_SMS_QUERY_KEY[1] &&
            key[2] === queryKeyPrefix[2] &&
            key[3] === queryKeyPrefix[3] &&
            key[4] === queryKeyPrefix[4]
          );
        },
      },
      (oldData: any) => {
        if (!oldData?.pages) return oldData;

        return {
          ...oldData,
          pages: oldData.pages.map((page: any) => ({
            ...page,
            data: (page.data ?? []).map((message: SmsMessage) => {
              if (message.uid === sms.uid) return { ...message, ...sms };
              if (
                sms.externalUid &&
                (message.externalUid === sms.externalUid ||
                  message.content?.SmsSid === sms.externalUid)
              ) {
                return { ...message, ...sms };
              }
              return message;
            }),
          })),
        };
      },
    );
  };

  useWatchSmsVrNotification<SmsMessage>({
    companieNumbers,
    notificationTypes: ["sms:new"],
    disableSnackbarAlert: true,
    onNotificationArrived: (sms) => {
      if (!sms) return;

      const ownership = resolveOwnership(sms, ownedNumbers, sessionNumber);
      if (!ownership) return;

      const company = userCompanies.find(
        (company) => String(company.companyId) === String(sms.companyId),
      );

      const enrichedSms: SmsMessageWithCompany = {
        ...sms,
        companyName: company?.company?.businessName || "",
      };

      queryClient.invalidateQueries({
        queryKey: getSmsContactsQueryKey(ownership.ownedNumber),
      });

      const queryKey = getChatSmsQueryKey(ownership.contactPhone, ownership.ownedNumber, sms.companyId, "");

      queryClient.setQueryData(queryKey, (oldData: any) => {
        if (!oldData?.pages) return oldData;

        const alreadyExists = oldData.pages.some((page: any) =>
          page.data?.some((message: SmsMessage) => message.uid === sms.uid),
        );

        if (alreadyExists) return oldData;

        return {
          ...oldData,
          pages: oldData.pages.map((page: any, index: number) => {
            if (index !== 0) return page;

            return {
              ...page,
              data: [enrichedSms, ...(page.data ?? [])],
            };
          }),
        };
      });

      queryClient.invalidateQueries({ queryKey });

      if (ownership.isIncoming) {
        onIncomingSms?.(enrichedSms);
      }
    },
  });

  useWatchSmsVrNotification<SmsMessage>({
    companieNumbers,
    notificationTypes: ["sms:updated"],
    disableSnackbarAlert: true,
    onNotificationArrived: (sms) => {
      if (!sms) return;
      patchMessageInChatCache(sms);
    },
  });
};
